import {ActionMeta, OptionsType, ValueType} from 'react-select';
import {logger} from '../../../helpers';

export type ReactSelectOnChangeCallback<T> = (value: ValueType<T>, action: ActionMeta<T>) => void;

function isOptionsType<T>(value: T | OptionsType<T>): value is OptionsType<T> {
  return Array.isArray(value);
}

function nullableReactSelectHandler<T>(
  name: string,
  handler: (t: T | null) => void): ReactSelectOnChangeCallback<T> {
  return (value: ValueType<T>, action: ActionMeta<T>) => {
    if (value === null || value === undefined) {
      handler(null);
      return;
    }
    if (isOptionsType(value)) {
      logger.error(`${name}: Bad value`, value, action);
      return;
    }
    handler(value);
  };
}

function nullableMappingReactSelectHandler<T, V>(
  name: string,
  mapper: (t: T) => V,
  handler: (t: V | null) => void): ReactSelectOnChangeCallback<T> {
  return (value: ValueType<T>, action: ActionMeta<T>) => {
    if (value === null || value === undefined) {
      handler(null);
      return;
    }
    if (isOptionsType(value)) {
      logger.error(`${name}: Bad value`, value, action);
      return;
    }
    handler(mapper(value));
  };
}

function singleReactSelectHandler<T>(
  name: string,
  handler: (t: T) => void): ReactSelectOnChangeCallback<T> {
  return (value: ValueType<T>, action: ActionMeta<T>) => {
    if (value === null || value === undefined || isOptionsType(value)) {
      logger.error(`${name}: Bad value`, value, action);
      return;
    }
    handler(value);
  };
}

function mappingSingleReactSelectHandler<T, V>(
  name: string,
  mapper: (t: T) => V,
  handler: (t: V) => void): ReactSelectOnChangeCallback<T> {
  return (value: ValueType<T>, action: ActionMeta<T>) => {
    if (value === null || value === undefined || isOptionsType(value)) {
      logger.error(`${name}: Bad value`, value, action);
      return;
    }
    handler(mapper(value));
  };
}

function mappingMultiReactSelectHandler<T, V>(
  name: string,
  mapper: (t: T) => V,
  handler: (t: V[]) => void): ReactSelectOnChangeCallback<T> {
  return (value: ValueType<T>, action: ActionMeta<T>) => {
    if (value === null || value === undefined)
      handler([]);
    else if (isOptionsType(value))
      handler(value.map(mapper));
    else
      handler([mapper(value)]);
  };
}

class ReactSelectHelperClass {
  readonly nullableSingle = nullableReactSelectHandler;
  readonly nullableMappingSingle = nullableMappingReactSelectHandler;
  readonly single = singleReactSelectHandler;
  readonly mappingSingle = mappingSingleReactSelectHandler;
  readonly mappingMulti = mappingMultiReactSelectHandler;
}

export const ReactSelectHelper = new ReactSelectHelperClass();
